import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useState } from 'react';
import { capitalizeFirstLetter, round } from '../../utils/utils';
import { capitalizeFirstWord } from '../../utils/utils';
import NRSecondaryLink from '../NRSecondaryLink/NRSecondaryLink';
import ColoredCircle from '../../NRIcons/ColoredCircle';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const ColoredBarTooltipContent = ({ title, color, value, showPercentage = false, total, capitalizeFirstOnly, capitalizeEachWord }) => {
  const theme = useTheme();
  return (
    <>
      {!!title && !!color && (!!value || value === 0) ? (
        <Box sx={{ display: 'flex' }}>
          <Box component="span" sx={{ marginRight: theme.spacing(1), display: 'inline-flex' }}>
            <ColoredCircle color={color} />
          </Box>
          <Box component="span" sx={{ marginRight: theme.spacing(1) }}>
            {`${capitalizeEachWord ? capitalizeFirstWord(title) : capitalizeFirstOnly ? capitalizeFirstLetter(title) : title}: ${value}`}{' '}
            {`${showPercentage ? '(' + round((value / total) * 100, 1) + '%)' : ''}`}
          </Box>
        </Box>
      ) : (
        ''
      )}
    </>
  );
};

const NRColoredBar = ({
  innerKey,
  config,
  emptyBarColor,
  showTotal = true,
  showValues = true,
  handleOnTotalClick,
  showPercentage = false,
  height = 24,
  capitalizeFirstOnly = true,
  capitalizeEachWord = true,
  isLoading,
  summaryLabel,
  disableOnClick = false
}) => {
  const theme = useTheme();
  const total = Array.isArray(config) && config.reduce((a, b) => a + b?.value, 0);
  const EMPTY_BAR_COLOR = emptyBarColor || theme.palette.noData.background;

  const a11ySummary = total
    ? `${summaryLabel || 'Results'} summary: ${config
        .map(item => {
          const percent = total ? round((item.value / total) * 100, 1) : 0;
          const label = item.title;
          return `${label} ${item.value} (${percent}%)`;
        })
        .join(', ')}. Total ${total}.`
    : `${summaryLabel || 'Results'} summary: No data available.`;

  return (
    <Box
      sx={{
        height,
        margin: 0,
        width: '100%',
        backgroundColor: 'transparent',
        borderRadius: 5,
        display: 'flex'
      }}
      role="group"
      aria-label={`Result distribution bar${total ? `, total ${total}` : ''}`}
    >
      <Typography variant="srOnly" id={`${innerKey}-summary`}>
        {a11ySummary}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          '& > div:last-child': {
            borderTopRightRadius: theme.shape.borderRadius,
            borderBottomRightRadius: theme.shape.borderRadius
          },
          '& > div:first-child': {
            borderTopLeftRadius: theme.shape.borderRadius,
            borderBottomLeftRadius: theme.shape.borderRadius
          }
        }}
      >
        {!!total && !isLoading ? (
          config.map(configItem => {
            const percent = total ? round((configItem.value / total) * 100, 1) : 0;
            const segmentLabel = `${configItem.title}: ${percent}% (${configItem.value})`;

            return (
              <NRColoredSegment
                key={`${innerKey}-${configItem.title}-${configItem.value}`}
                width={(configItem.value * 100) / total}
                color={configItem.color}
                value={configItem.value}
                tooltipTitle={
                  !!configItem?.toolTip ? (
                    configItem.toolTip
                  ) : (
                    <ColoredBarTooltipContent
                      title={configItem?.title}
                      color={configItem.color}
                      value={configItem.value}
                      showPercentage={showPercentage}
                      total={total}
                      capitalizeFirstOnly={capitalizeFirstOnly}
                      capitalizeEachWord={capitalizeEachWord}
                    />
                  )
                }
                showValues={showValues}
                handleOnClick={!disableOnClick ? configItem?.action : undefined}
                ariaLabel={segmentLabel}
              />
            );
          })
        ) : (
          <NRColoredSegment width={100} color={EMPTY_BAR_COLOR} showValues={false} ariaLabel="No data available" disableOnClick={disableOnClick} />
        )}
      </Box>
      {showTotal && (
        <Box
          sx={{
            marginLeft: theme.spacing(1),
            minWidth: theme.spacing(3),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: !disableOnClick ? 'pointer' : 'default'
          }}
        >
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <NRSecondaryLink
              value={total}
              disabledClick={disableOnClick}
              handleOnClick={!disableOnClick ? () => handleOnTotalClick() : undefined}
              aria-label={`Total value: ${total}`}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const NRColoredSegment = ({
  width,
  color,
  value,
  tooltipTitle,
  showValues,
  valueDisplayWithThreshold = 24,
  handleOnClick,
  ariaLabel,
  disableOnClick = false
}) => {
  const mySegment = React.createRef();
  const [segmentWidth, setSegmentWidth] = useState();
  const theme = useTheme();
  const isInteractive = !!handleOnClick && !disableOnClick;

  useEffect(() => {
    !!mySegment?.current?.offsetWidth && setSegmentWidth(mySegment?.current?.offsetWidth);
  }, [mySegment?.current?.offsetWidth]);

  return value !== 0 || !tooltipTitle ? (
    <Tooltip title={tooltipTitle || ''} placement={'top'}>
      <Box
        ref={mySegment}
        onClick={!disableOnClick ? () => handleOnClick && handleOnClick() : undefined}
        onKeyDown={event => {
          if (!isInteractive) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleOnClick();
          }
        }}
        sx={{
          width: `${width}%`,
          backgroundColor: color,
          color: theme.palette.white,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: theme.spacing(1),
          cursor: isInteractive ? 'pointer' : 'default'
        }}
        role={isInteractive ? 'button' : 'img'}
        tabIndex={isInteractive ? 0 : -1}
        aria-label={ariaLabel}
      >
        {showValues && segmentWidth > valueDisplayWithThreshold && value}
      </Box>
    </Tooltip>
  ) : (
    <></>
  );
};

export default NRColoredBar;
